<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  businessData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['showDetail']);
const { t, te } = useI18n();

const user = computed(() => props.businessData?.user || {});
const summary = computed(() => props.businessData?.summary || {});
const latestOrder = computed(() => props.businessData?.orders?.[0] || null);
const activeSubscription = computed(
  () =>
    props.businessData?.subscriptions?.find(item => item.status === 'active') ||
    props.businessData?.subscriptions?.[0] ||
    null
);

const translateStatus = (group, value) => {
  if (!value) return '';
  const key = `CONVERSATION_SIDEBAR.AIPAYOK.${group}.${String(value).toUpperCase()}`;
  return te(key) ? t(key) : t('CONVERSATION_SIDEBAR.AIPAYOK.UNKNOWN_STATUS');
};

const summaryItems = computed(() =>
  [
    {
      key: 'user',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.BUSINESS_USER_ID'),
      value: user.value.id,
    },
    {
      key: 'balance',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.BALANCE'),
      value:
        user.value.balance === undefined
          ? null
          : `¥${Number(user.value.balance).toFixed(2)}`,
    },
    {
      key: 'invite',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.INVITE_CODE'),
      value: user.value.inviteCode,
    },
    {
      key: 'orders',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.ORDER_COUNT'),
      value: summary.value.orderCount,
    },
    {
      key: 'subscriptions',
      label: t('CONVERSATION_SIDEBAR.AIPAYOK.ACTIVE_SUBSCRIPTIONS'),
      value: summary.value.activeSubscriptionCount,
    },
  ].filter(
    item => item.value !== null && item.value !== undefined && item.value !== ''
  )
);
</script>

<template>
  <section
    v-if="businessData"
    class="mx-3 mb-2 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2"
  >
    <div class="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1">
      <span
        v-for="item in summaryItems"
        :key="item.key"
        class="flex items-center gap-1 text-xs"
      >
        <span class="text-n-slate-10">{{ item.label }}</span>
        <strong class="font-medium text-n-slate-12">{{ item.value }}</strong>
      </span>

      <span v-if="latestOrder" class="flex min-w-0 items-center gap-1 text-xs">
        <span class="text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.LATEST_ORDER_NO') }}
        </span>
        <strong
          class="max-w-40 truncate font-medium text-n-brand"
          :title="latestOrder.orderNo"
        >
          {{ latestOrder.orderNo }}
        </strong>
        <span class="max-w-36 truncate text-n-slate-12">
          {{ latestOrder.planName }}
        </span>
        <span class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-n-slate-11">
          {{ translateStatus('ORDER_STATUS', latestOrder.status) }}
        </span>
      </span>

      <span
        v-if="activeSubscription"
        class="flex min-w-0 items-center gap-1 text-xs"
      >
        <span class="text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.SUBSCRIPTIONS') }}
        </span>
        <strong class="max-w-36 truncate font-medium text-n-slate-12">
          {{ activeSubscription.planName }}
        </strong>
        <span class="rounded bg-n-teal-3 px-1.5 py-0.5 text-n-teal-11">
          {{
            translateStatus('SUBSCRIPTION_STATUS', activeSubscription.status)
          }}
        </span>
      </span>
    </div>

    <button
      type="button"
      class="shrink-0 text-xs font-medium text-n-brand hover:underline"
      @click="emit('showDetail')"
    >
      {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.OPEN_USER') }}
    </button>
  </section>
</template>
